import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import loadable from '@wix/yoshi-flow-editor/loadable';
import classNames from 'classnames';
import React, {useCallback, useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import {useGlobals, withGlobalProps} from '../../../providers/globalPropsProvider';
import {RenderContext, RenderContextProvider} from '../../../providers/renderContext';
import {StickyButton} from './StickyButton/StickyButton';
import s from './StickyButtonContainer.scss';
import type {StickyOptionsDrawerProps} from './StickyOptionsDrawer/StickyOptionsDrawer';
import {useStickyButtonText} from './useStickyButtonText';
import {useStickyVisibility} from './useStickyVisibility';
import {useStickyTransition} from './useStickyTransition';
import {StickyButtonAction} from '../../../constants';
import stylesParams from '../../../../components/ProductPage/stylesParams';

const LazyStickyOptionsDrawer = loadable<StickyOptionsDrawerProps>(
  () =>
    import(
      /* webpackChunkName: "StickyOptionsDrawer", webpackPrefetch: true */ './StickyOptionsDrawer/StickyOptionsDrawer'
    ),
  {
    resolveComponent: (module) => module.StickyOptionsDrawer,
  }
);

type StickyButtonContainerProps = IProvidedTranslationProps;

export enum StickyButtonContainerDataHook {
  Root = 'StickyButtonContainerDataHook.Root',
}

const StickyButtonContainerComponent: React.FC<StickyButtonContainerProps> = ({t}) => {
  const {
    handleAddToCart,
    handleBuyNow,
    accessibilityEnabled,
    styles,
    host,
    isUserInputsValid,
    isEditorX,
    isStickyDrawerOpenInEditor,
  } = useGlobals();
  const liveIsBuyNow = styles.get(stylesParams.productPage_stickyButtonAction) === StickyButtonAction.BUY_NOW;
  const isVisible = useStickyVisibility();
  //Fix for https://wix.atlassian.net/browse/GPV-6653
  const isClassicEditor = !isEditorX;

  const [isMounted, setIsMounted] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const {state, appliedIsBuyNow, handleAnimationEnd} = useStickyTransition(isVisible, liveIsBuyNow);
  const buttonText = useStickyButtonText(t, appliedIsBuyNow);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleStickyButtonClick = useCallback(() => {
    if (isUserInputsValid) {
      appliedIsBuyNow ? handleBuyNow(accessibilityEnabled, true) : handleAddToCart(true);
      return;
    }
    setIsDrawerOpen(true);
  }, [isUserInputsValid, appliedIsBuyNow, handleBuyNow, accessibilityEnabled, handleAddToCart]);

  const handleDrawerClose = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const isOpen = isDrawerOpen || isStickyDrawerOpenInEditor;

  if (!isMounted) {
    return null;
  }

  return createPortal(
    <RenderContextProvider value={RenderContext.StickyDrawer}>
      <div
        className={classNames(s.stickyContainer, host.id, {
          [s.hidden]: state === 'hidden',
          [s.visible]: state === 'visible',
          [s.exiting]: state === 'exiting',
          [s.classicEditorMode]: isClassicEditor,
        })}
        data-hook={StickyButtonContainerDataHook.Root}
        onAnimationEnd={handleAnimationEnd}>
        <StickyButton text={buttonText} onClick={handleStickyButtonClick} isBuyNow={appliedIsBuyNow} />
      </div>
      {isOpen && <LazyStickyOptionsDrawer isOpen={isOpen} onClose={handleDrawerClose} buttonText={buttonText} />}
    </RenderContextProvider>,
    document.body
  );
};

export const StickyButtonContainer = withGlobalProps(withTranslations('globals.texts')(StickyButtonContainerComponent));
